/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 */
import { t } from 'i18next';
import QRCode from "qrcode";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { GetQrCode } from "../../../reduxToolkit/CompanyEmployees/CompanyEmployeesApi";
import {getFullName, isEmailFormat, userDataTransform} from "../../../Apis/functions";
import {ErrorCode} from "../../../Apis/ErrorCode";
import {colors} from "../../../enums/colors";
import {STATUS_MODEL} from "../../../enums/statusEnum";
import {
    IconApprovedCard,
    IconChipMultiplePerson, IconDashedCircleList,
    IconFamily,
    IconListButton,
    IconPersonCheck,
    IconPersonHouse,
    IconPersonPlusCard,
    IconRequestRemaining,
    IconStarCard,
    IconTitleEntryMethod,
    IconTwoPersons,
    PersonLineIcon
} from "../../../components/buttons/IconButtons";

/**
 * Resources assigned to a building, one section per resource in the detail expand.
 */
export const BUILDING_RESOURCE = {
    RESIDENTS: 'RESIDENTS',
    UNITS: 'UNITS'
}

/**
 * Backend "resourceString" selector for the assigned building resource endpoints.
 * The server uses it to pick the per-mode shape.
 */
export const BUILDING_RESOURCE_MODE = {
    [BUILDING_RESOURCE.RESIDENTS]: 'resident',
    [BUILDING_RESOURCE.UNITS]: 'unit'
}

/**
 * Builds the inner Add/Assigned tab list of a building resource section.
 *
 * On create (no id) only the Add tab (key 0) is shown; on update (id present)
 * the Assigned tab (key 1) is added too.
 *
 * @param {Object} tabs - Tab config object.
 * @returns {(id: number|string|null|undefined) => Array<Object>} Resolver.
 */
const getTabsById = (tabs) => (id) => {
    const hasId = id !== null && id !== undefined;
    return Object.values(tabs)
        .filter(e => typeof e === 'object' && e.key !== undefined)
        .filter(e => hasId || e.key === 0);
};

/**
 * Add/Assigned tabs of the residents section rendered at the bottom of the
 * update building modal. Mirrors CUSTOM_ACCESS_TABS of the shift access module.
 */
export const BUILDING_RESIDENTS_TABS = {
    ADD_EMPLOYEES: {
        key: 0,
        name: "ADD_EMPLOYEES",
        translation: "add_employees",
        icon: <IconPersonPlusCard width={24} height={24}/>,
    },
    ASSIGNED_EMPLOYEES: {
        key: 1,
        name: "ASSIGNED_EMPLOYEES",
        translation: "assigned_employees",
        icon: <IconChipMultiplePerson width={24} height={24}/>,
    },
    getTabsById: getTabsById({}),
}

BUILDING_RESIDENTS_TABS.getTabsById = getTabsById(BUILDING_RESIDENTS_TABS);


/**
 * Verification status of a resident, shown as a chip next to their name.
 */
export const RESIDENT_STATUS = {
    VERIFIED: 'VERIFIED',
    UNVERIFIED: 'UNVERIFIED'
}

/**
 * Verification status the card paints for a resident, resolved from the identity
 * validation the service answers: only a validated one counts as verified, any
 * other status (and the residents that never started it) stays unverified.
 *
 * @param {number|null|undefined} identityValidationId - Identity validation status of the resident.
 * @returns {string} One of RESIDENT_STATUS.
 */
export const getResidentVerificationStatus = (identityValidationId) =>
    identityValidationId === STATUS_MODEL.VALIDATED
        ? RESIDENT_STATUS.VERIFIED
        : RESIDENT_STATUS.UNVERIFIED;

/**
 * Downloads the access QR of the logged resident as a PNG file.
 *
 * The QR belongs to the session that asks for it, the endpoint takes no user, it
 * is drawn 300px wide and given to the browser through a temporary link. Shared by
 * the registered residents of the building detail and by the household members of
 * my apartment.
 *
 * @param {Function} dispatch - Redux dispatch of the caller.
 */
export const downloadResidentQr = async (dispatch) => {
    const { payload } = await dispatch(GetQrCode());
    const { data: responseData, success } = payload?.data ?? {};
    if (success !== true || !responseData) return;

    try {
        // The service answers the body of the QR as an object, the code carries it
        // serialized the same way the dining ones do
        const qrData = typeof responseData === 'string' ? responseData : JSON.stringify(responseData);
        const image = await QRCode.toDataURL(qrData, { width: 300 });
        const link = document.createElement('a');
        link.href = image;
        link.download = t("qr_code") + '.png';
        link.click();
    } catch (err) {
        toast.error(t('something_went_wrong_2'));
    }
};

/**
 * Identity validation status each verification card searches with, it travels as
 * the viewType of the building_apartment_resident_identity model so the service
 * only returns the residents of the classification that opened the modal.
 */
export const RESIDENT_STATUS_VIEW_TYPE = {
    [RESIDENT_STATUS.VERIFIED]: STATUS_MODEL.VALIDATED,
    [RESIDENT_STATUS.UNVERIFIED]: STATUS_MODEL.PENDING_TO_UPLOAD_INFORMATION
}

/**
 * Relationship of a resident with the unit, shown as a chip next to their name.
 */
export const RESIDENT_TYPE = {
    OWNER: 'OWNER',
    TENANT: 'TENANT',
    FAMILY_MEMBER: 'FAMILY_MEMBER',
    AUTHORIZED_OCCUPANT: 'AUTHORIZED_OCCUPANT'
}

/**
 * Relationship of a household member with the main resident of the unit, the
 * card renders it as a chip next to the email.
 */
export const RELATIONSHIP_TYPE = {
    SPOUSE: 'SPOUSE',
    CHILD: 'CHILD',
    PARENT: 'PARENT',
    SIBLING: 'SIBLING',
    GRANDPARENT: 'GRANDPARENT',
    GRANDCHILD: 'GRANDCHILD',
    COUSIN: 'COUSIN',
    UNCLE_AUNT: 'UNCLE_AUNT',
    NEPHEW_NIECE: 'NEPHEW_NIECE',
    PARTNER: 'PARTNER',
    OTHER: 'OTHER'
}

/**
 * Chip appearance shared by every household member relationship, only the
 * translation changes between them.
 */
const RESIDENT_RELATIONSHIP_CHIP = {
    background: colors.secondary100,
    fontColor: colors.secondary700,
    borderColor: colors.secondary150,
    icon: '',
    classNameIcon: ''
}

/**
 * Chip appearance (translation + colors) for each household member relationship.
 */
export const RELATIONSHIP_TYPE_CHIP = {
    [RELATIONSHIP_TYPE.SPOUSE]: { ...RESIDENT_RELATIONSHIP_CHIP, translation: 'spouse' },
    [RELATIONSHIP_TYPE.CHILD]: { ...RESIDENT_RELATIONSHIP_CHIP, translation: 'child' },
    [RELATIONSHIP_TYPE.PARENT]: { ...RESIDENT_RELATIONSHIP_CHIP, translation: 'parent' },
    [RELATIONSHIP_TYPE.SIBLING]: { ...RESIDENT_RELATIONSHIP_CHIP, translation: 'sibling' },
    [RELATIONSHIP_TYPE.GRANDPARENT]: { ...RESIDENT_RELATIONSHIP_CHIP, translation: 'grandparent' },
    [RELATIONSHIP_TYPE.GRANDCHILD]: { ...RESIDENT_RELATIONSHIP_CHIP, translation: 'grandchild' },
    [RELATIONSHIP_TYPE.COUSIN]: { ...RESIDENT_RELATIONSHIP_CHIP, translation: 'cousin' },
    [RELATIONSHIP_TYPE.UNCLE_AUNT]: { ...RESIDENT_RELATIONSHIP_CHIP, translation: 'uncle_aunt' },
    [RELATIONSHIP_TYPE.NEPHEW_NIECE]: { ...RESIDENT_RELATIONSHIP_CHIP, translation: 'nephew_niece' },
    [RELATIONSHIP_TYPE.PARTNER]: { ...RESIDENT_RELATIONSHIP_CHIP, translation: 'partner' },
    [RELATIONSHIP_TYPE.OTHER]: { ...RESIDENT_RELATIONSHIP_CHIP, translation: 'other' },
}

/**
 * Chip appearance (translation + colors) for each resident status.
 */
export const RESIDENT_STATUS_CHIP = {
    [RESIDENT_STATUS.VERIFIED]: {
        translation: 'verified',
        background: colors.success100,
        fontColor: colors.success700,
        borderColor: colors.success150,
        icon: IconApprovedCard,
        classNameIcon: 'icon-color-success-700 w-16px h-16px',
        variant: 'large'
    },
    [RESIDENT_STATUS.UNVERIFIED]: {
        translation: 'unverified',
        background: colors.warning100,
        fontColor: colors.warning700,
        borderColor: colors.warning150,
        icon: IconRequestRemaining,
        classNameIcon: 'icon-color-warning-700 w-16px h-16px',
        variant: 'large'
    },
}

/**
 * Chip appearance (translation + colors) for each resident type, the one the
 * card paints next to the name.
 */
export const RESIDENT_TYPE_CHIP = {
    [RESIDENT_TYPE.OWNER]: {
        translation: 'owner',
        background: colors.warning100,
        fontColor: colors.warning700,
        borderColor: colors.warning150,
        icon: IconStarCard,
        classNameIcon: 'icon-color-warning-700 w-16px h-16px',
        variant: 'compact'
    },
    [RESIDENT_TYPE.FAMILY_MEMBER]: {
        translation: 'family_member',
        background: colors.secondary100,
        fontColor: colors.secondary700,
        borderColor: colors.secondary150,
        icon: IconFamily,
        classNameIcon: 'icon-color-secondary-700 w-16px h-16px',
        variant: 'compact'
    },
    [RESIDENT_TYPE.TENANT]: {
        translation: 'tenant',
        background: colors.secondary100,
        fontColor: colors.secondary700,
        borderColor: colors.secondary150,
        icon: IconPersonHouse,
        classNameIcon: 'icon-color-secondary-700 w-16px h-16px',
        variant: 'compact'
    },
    [RESIDENT_TYPE.AUTHORIZED_OCCUPANT]: {
        translation: 'authorized_occupant',
        background: colors.secondary100,
        fontColor: colors.secondary700,
        borderColor: colors.secondary150,
        icon: IconPersonCheck,
        classNameIcon: 'icon-color-secondary-700 w-16px h-16px',
        variant: 'compact'
    },
}

export const BUILDING_DETAILS_MAIN_TABS_MODEL = {
    RESIDENTS: {
        id: 0,
        key: "RESIDENTS",
        translation: "residents",
        icon: <IconTwoPersons width={16} height={16}/>
    },
    UNITS: {
        id: 1,
        key: "UNITS",
        translation: "assigned_units",
        icon: <IconTitleEntryMethod width={16} height={16}/>
    }
}

/**
 * Building detail rendered by the information card of the details view, filled
 * with the building returned by building-service/v1/building/get/{id}.
 *
 * The address is not part of the response, the view resolves it from the
 * coordinates of the building.
 */
export const BUILDING_DETAIL_MODEL = {
    id: null,
    name: '',
    statusId: '',
    zoneId: '',
    zoneName: '',
    latitude: '',
    longitude: '',
    location: '',
    totalResidents: 0,
    validatedResidents: 0,
    pendingValidationResidents: 0,
    /**
     * Fills the detail with the building returned by the service, the status
     * and the zone come as objects on the detail and as ids on the list row.
     *
     * @param {Object} data - Building coming from the backend.
     * @returns {Object} Detail ready to be rendered.
     */
    set: function (data) {
        return {
            ...this,
            id: data?.id ?? null,
            name: data?.name ?? '',
            statusId: data?.status?.id ?? data?.statusId ?? data?.status ?? '',
            zoneId: data?.zone?.id ?? data?.zoneId ?? '',
            zoneName: data?.zone?.name ?? data?.zoneName ?? '',
            latitude: data?.latitude ?? data?.latitud ?? '',
            longitude: data?.longitude ?? data?.longitud ?? '',
            location: '',
            totalResidents: data?.totalResidents ?? 0,
            validatedResidents: data?.validatedResidents ?? 0,
            pendingValidationResidents: data?.pendingValidationResidents ?? 0
        }
    }
}

/**
 * TODO: temporary mock, remove it once building-service exposes the residents
 * of a building and the list comes from the real endpoint.
 *
 * Every resident carries the household members of its apartment, they are the
 * ones the details modal lists under the contact information.
 */
export const BUILDING_RESIDENTS_MOCK = [
    {
        id: 1, fullName: 'María Fernanda López', email: 'maria.lopez@gmail.com', apartment: '101', status: RESIDENT_STATUS.VERIFIED,
        members: [
            { id: 12, fullName: 'Diego López Ramos', email: 'diego.lopez@gmail.com', relationship: RELATIONSHIP_TYPE.SPOUSE, status: RESIDENT_TYPE.AUTHORIZED_OCCUPANT },
            { id: 13, fullName: 'Renata López Ramos', email: 'renata.lopez@gmail.com', relationship: RELATIONSHIP_TYPE.CHILD, status: RESIDENT_TYPE.FAMILY_MEMBER },
        ]
    },
    {
        id: 2, fullName: 'Juan Pérez Hernández', email: 'juan.perez@gmail.com', apartment: '102', status: RESIDENT_STATUS.UNVERIFIED,
        members: []
    },
    {
        id: 3, fullName: 'Carlos Alberto Ramírez', email: 'carlos.ramirez@gmail.com', apartment: '201', status: RESIDENT_STATUS.VERIFIED,
        members: [
            { id: 32, fullName: 'Lucía Ramírez Soto', email: 'lucia.ramirez@gmail.com', relationship: RELATIONSHIP_TYPE.SPOUSE, status: RESIDENT_TYPE.AUTHORIZED_OCCUPANT },
            { id: 33, fullName: 'Emilio Ramírez Soto', email: 'emilio.ramirez@gmail.com', relationship: RELATIONSHIP_TYPE.CHILD, status: RESIDENT_TYPE.AUTHORIZED_OCCUPANT },
            { id: 34, fullName: 'Ximena Soto Vargas', email: 'ximena.soto@gmail.com', relationship: RELATIONSHIP_TYPE.PARENT, status: RESIDENT_TYPE.AUTHORIZED_OCCUPANT },
        ]
    },
    {
        id: 4, fullName: 'Ana Sofía Martínez', email: 'ana.martinez@gmail.com', apartment: '202', status: RESIDENT_STATUS.UNVERIFIED,
        members: [
            { id: 42, fullName: 'Pablo Martínez Cano', email: 'pablo.martinez@gmail.com', relationship: RELATIONSHIP_TYPE.PARENT, status: RESIDENT_TYPE.AUTHORIZED_OCCUPANT },
        ]
    },
]

/**
 * TODO: temporary mock, remove it once building-service exposes the units of a
 * building and the list comes from the real endpoint.
 */
export const BUILDING_UNITS_MOCK = [
    {
        id: 1,
        unitName: 'Unit 101',
        totalResidents: 3,
        mainResident: { id: 11, fullName: 'María Fernanda López', email: 'maria.lopez@gmail.com', status: RESIDENT_TYPE.OWNER },
        members: [
            { id: 12, fullName: 'Diego López Ramos', email: 'diego.lopez@gmail.com',  relationship: RELATIONSHIP_TYPE.SPOUSE, status: RESIDENT_TYPE.TENANT },
            { id: 13, fullName: 'Renata López Ramos', email: 'renata.lopez@gmail.com', relationship: RELATIONSHIP_TYPE.CHILD, status: RESIDENT_TYPE.AUTHORIZED_OCCUPANT },
        ]
    },
    {
        id: 2,
        unitName: 'Unit 102',
        totalResidents: 1,
        mainResident: { id: 21, fullName: 'Juan Pérez Hernández', email: 'juan.perez@gmail.com', status: RESIDENT_TYPE.OWNER },
        members: []
    },
    {
        id: 3,
        unitName: 'Unit 201',
        totalResidents: 4,
        mainResident: { id: 31, fullName: 'Carlos Alberto Ramírez', email: 'carlos.ramirez@gmail.com', status: RESIDENT_TYPE.OWNER },
        members: [
            { id: 32, fullName: 'Lucía Ramírez Soto', email: 'lucia.ramirez@gmail.com', relationship: RELATIONSHIP_TYPE.SPOUSE, status: RESIDENT_TYPE.TENANT },
            { id: 33, fullName: 'Emilio Ramírez Soto', email: 'emilio.ramirez@gmail.com', relationship: RELATIONSHIP_TYPE.CHILD, status: RESIDENT_TYPE.AUTHORIZED_OCCUPANT },
            { id: 34, fullName: 'Ximena Soto Vargas', email: 'ximena.soto@gmail.com', relationship: RELATIONSHIP_TYPE.PARENT, status: RESIDENT_TYPE.TENANT },
        ]
    },
    {
        id: 4,
        unitName: 'Unit 202',
        totalResidents: 2,
        mainResident: { id: 41, fullName: 'Ana Sofía Martínez', email: 'ana.martinez@gmail.com', status: RESIDENT_TYPE.OWNER },
        members: [
            { id: 42, fullName: 'Pablo Martínez Cano', email: 'pablo.martinez@gmail.com', relationship: RELATIONSHIP_TYPE.PARENT, status: RESIDENT_TYPE.TENANT  },
        ]
    },
    {
        id: 5,
        unitName: 'Unit 301',
        totalResidents: 2,
        mainResident: { id: 51, fullName: 'Rodrigo Salinas Vega', email: 'rodrigo.salinas@gmail.com', status: RESIDENT_TYPE.OWNER },
        members: [
            { id: 52, fullName: 'Valeria Vega Ruiz', email: 'valeria.vega@gmail.com', relationship: RELATIONSHIP_TYPE.SIBLING, status: RESIDENT_TYPE.TENANT  },
        ]
    },
]

/**
 * Shapes the building_apartment_resident rows of one apartment as the resident
 * card renders them, both details modals read the same endpoint.
 *
 * @param {Array} data - building_apartment_resident rows of the apartment.
 * @returns {Array} Residents of the apartment ready for the card.
 */
export const setApartmentResidents = (data) => (data || []).map((resident) => ({
    id: resident?.id ?? null,
    userId: resident?.userId ?? null,
    buildingApartmentId: resident?.buildingApartmentId ?? null,
    fullName: getFullName(resident?.name, resident?.lastName),
    email: resident?.email ?? '',
    // Resident type of the unit, painted as the chip next to the name,
    // the residents without one fall back to their verification status
    status: resident?.residentType || getResidentVerificationStatus(resident?.identityValidationId),
    // Relationship with the main resident, painted next to the email
    relationship: resident?.relationshipType ?? '',
    verificationStatus: getResidentVerificationStatus(resident?.identityValidationId),
    residentId: resident?.residentId ?? null,
    residentFullName: resident?.residentFullName ?? '',
    residentEmail: resident?.residentEmail ?? ''
}));

/**
 * Residents of one unit rendered by the details modal of the units tab, filled
 * with the rows returned by building-service/v1/building-apartment/get/{id}.
 *
 * The service answers the residents of the apartment in one flat list, the
 * owner of it is the main resident and the rest are the household members
 * registered under them.
 */
export const UNIT_DETAIL_MODEL = {
    apartment: '',
    mainResident: null,
    members: [],
    /**
     * Splits the residents of the apartment into the main resident and the
     * household members, both shaped as the resident card expects them.
     *
     * @param {Array} data - building_apartment_resident rows of the apartment.
     * @returns {Object} Unit ready to be rendered by the details modal.
     */
    set: function (data) {
        const residents = setApartmentResidents(data);

        const mainResident = residents.find((resident) => resident.status === RESIDENT_TYPE.OWNER) || null;

        return {
            ...this,
            apartment: data?.[0]?.apartment ?? '',
            // The relationship chip only makes sense between a member and the
            // main resident, the owner is the one the rest are related to
            mainResident: mainResident ? { ...mainResident, relationship: '' } : null,
            members: residents.filter((resident) => resident !== mainResident)
        }
    }
}

/**
 * Household of one registered resident, rendered by the details modal of the
 * registered residents tab with the rows returned by
 * building-service/v1/building-apartment/get/{id}.
 *
 * The service answers every resident of the apartment, the one the modal is
 * showing is already painted on the header so only the rest are members here.
 */
export const RESIDENT_HOUSEHOLD_MODEL = {
    apartment: '',
    owner: null,
    members: [],
    /**
     * Keeps the owner of the apartment and the residents that share it with the
     * one shown, both the owner and the resident of the card are left out of the
     * household members.
     *
     * @param {Array} data - building_apartment_resident rows of the apartment.
     * @param {string} residentId - Resident of the card, left out of the list.
     * @returns {Object} Household ready to be rendered by the details modal.
     */
    set: function (data, residentId) {
        const residents = setApartmentResidents(data);
        // The resident type travels as the status of the card
        const owner = residents.find((resident) => resident.status === RESIDENT_TYPE.OWNER) || null;

        return {
            ...this,
            apartment: data?.[0]?.apartment ?? '',
            owner,
            // The owner has their own section, only the rest of the apartment
            // is left as the household members of the resident shown
            members: residents.filter((resident) => resident.id !== residentId && resident !== owner)
        }
    }
}

export const RESIDENTS_TABS_MODEL = {
    REGISTERED: {
        id: 0,
        key: "REGISTERED",
        translation: "registered",
        icon: <PersonLineIcon width={16} height={16}/>
    },
    WHITE_LIST: {
        id: 1,
        key: "WHITE_LIST",
        translation: "white_list",
        icon: <IconListButton width={16} height={16}/>
    }
}


/**
 * Row captured on the manual tab of the white list modal, one per resident
 * authorized to register in the building.
 */
export const BUILDING_WHITE_LIST_MANUAL_MODEL = {
    id: null,
    name: '',
    lastName: '',
    email: '',
    unit: '',
    /**
     * True when the row was left untouched, the rows like this one are ignored
     * so an empty manual tab does not block the file of the automatic one.
     */
    isEmpty: function () {
        return `${this.name ?? ''}`.trim() === ''
            && `${this.lastName ?? ''}`.trim() === ''
            && `${this.email ?? ''}`.trim() === ''
            && `${this.unit ?? ''}`.trim() === '';
    },
    isValid: function () {
        return this.name !== null && this.name !== ''
            && this.lastName !== null && this.lastName !== ''
            && this.unit !== null && this.unit !== ''
            && this.email !== null && this.email !== '' && isEmailFormat(this.email);
    },
    /**
     * Payload row of building-service/v1/resident/white-list/create-all.
     *
     * @param {string} buildingId - Building the resident is authorized in.
     * @returns {Object} Entry expected by the create all endpoint.
     */
    toRegister: function (buildingId) {
        return {
            buildingId,
            name: this.name,
            lastName: this.lastName,
            email: this.email,
            apartment: this.unit
        }
    }
}

/**
 * File model of the automatic tab, it turns the template picked on the photo
 * picker into the multipart body of building-service/v1/resident/white-list/upload.
 */
export const BUILDING_WHITE_LIST_AUTOMATIC_MODEL = {
    buildingId: null,
    file: null,
    /**
     * Multipart payload of the upload endpoint.
     *
     * @param {string} buildingId - Building the residents are authorized in.
     * @param {Object} photoPickerFile - State of the photo picker of the automatic tab.
     * @returns {FormData} Body expected by the upload endpoint.
     */
    toRegister: function ({ buildingId, photoPickerFile }) {
        let formData = new FormData();
        formData.append('buildingId', buildingId);
        formData.append('file', photoPickerFile?.fileSelected);

        return formData;
    },
    isValid: function ({ photoPickerFile }) {
        return !!photoPickerFile?.fileSelected;
    }
}

/**
 * Form model of the white list modal, it holds the residents captured on the
 * manual tab. The automatic tab keeps its file on its own photo picker state.
 */
export const BUILDING_WHITE_LIST_MODEL = {
    whiteListManual: [BUILDING_WHITE_LIST_MANUAL_MODEL],
    /**
     * True when at least one row of the manual tab was captured, an untouched
     * manual tab is not sent so the automatic one can be saved on its own.
     */
    hasManualData: function () {
        return this.whiteListManual.filter((e) => !e.isEmpty()).length > 0;
    },
    isValidWhiteList: function () {
        return this.whiteListManual.filter((e) => !e.isValid()).length === 0;
    },
    toRegister: function (buildingId) {
        return this.whiteListManual
            .filter((e) => e.isValid())
            .map((e) => e.toRegister(buildingId));
    }
}

/**
 * Columns of the white list import grid, in the order the failed rows are
 * painted so the user can correct them and send them again.
 */
export const WHITE_LIST_IMPORT_COLUMNS = ['name', 'lastName', 'email', 'apartment'];

/**
 * Column of the import grid each failure reason belongs to. The create all
 * endpoint returns the reason as an error code and the grid paints the cell it
 * points at, the reasons out of this map only paint the error card.
 */
export const WHITE_LIST_IMPORT_ERROR_COLUMN = {
    [ErrorCode.BUILDING_BUILDING_RESIDENT_NAME_MISSING]: 0,
    [ErrorCode.BUILDING_BUILDING_RESIDENT_LAST_NAME_MISSING]: 1,
    [ErrorCode.BUILDING_BUILDING_RESIDENT_EMAIL_MISMATCH]: 2,
    [ErrorCode.BUILDING_BUILDING_RESIDENT_APARTMENT_MISSING]: 3,
    [ErrorCode.BUILDING_BUILDING_DEPARTMENT_ALREADY_EXISTS]: 3,
    [ErrorCode.BUILDING_BUILDING_DEPARTMENT_ALREADY_EXISTS_IN_SAME_LIST]: 3
}

/**
 * Rows the template of the white list keeps before the residents start, the
 * first one holds the titles of the columns. The grid of the import errors view
 * paints them as they come so the sheet it sends back keeps the same shape the
 * upload endpoint reads.
 */
export const WHITE_LIST_IMPORT_HEADER_ROWS = 1;

/**
 * Name given to the sheet rebuilt by the import errors view and to the file it
 * uploads with the corrected rows.
 */
export const WHITE_LIST_IMPORT_SHEET_NAME = 'Sheet1';
export const WHITE_LIST_IMPORT_FILE_NAME = 'white-list-errors.xlsx';

/**
 * Turns one row of the sheet into the values of the import grid, the columns
 * out of the template are dropped and the missing ones are left empty.
 *
 * @param {Array} row - Row read from the uploaded template.
 * @returns {Array} Values in WHITE_LIST_IMPORT_COLUMNS order.
 */
export const whiteListSheetRowToValues = (row) => WHITE_LIST_IMPORT_COLUMNS
    .map((_, index) => `${row?.[index] ?? ''}`);

/**
 * Turns one failed row into the values of the import grid. It is the fallback
 * of the rows the sheet does not hold, the ones captured on the manual tab.
 *
 * @param {Object} failure - Failed row returned by the white list endpoints.
 * @returns {Array} Values in WHITE_LIST_IMPORT_COLUMNS order.
 */
export const whiteListFailureToValues = (failure) => WHITE_LIST_IMPORT_COLUMNS
    .map((column) => `${failure?.[column] ?? ''}`);

/**
 * True when one row of the grid holds the same values as one of the header rows
 * it is compared against.
 *
 * @param {Array} row - Row of the grid, in WHITE_LIST_IMPORT_COLUMNS order.
 * @param {Array} headerRows - Header rows of the template.
 * @returns {boolean} True when the row is a copy of one of the header rows.
 */
export const isWhiteListHeaderRow = (row, headerRows) => (headerRows || [])
    .slice(0, WHITE_LIST_IMPORT_HEADER_ROWS)
    .some((header) => WHITE_LIST_IMPORT_COLUMNS.every(
        (_, index) => `${row?.[index] ?? ''}`.trim().toLowerCase() === `${header?.[index] ?? ''}`.trim().toLowerCase()
    ));

/**
 * True when the failed row returned by the upload endpoint is the header of the
 * template. The endpoint reads the titles of the columns as one more resident
 * and returns them as a failure, the header is not a row to be corrected and is
 * left out of the grid and of the error cards.
 *
 * @param {Object} failure - Failed row returned by the white list endpoints.
 * @param {Array} headerRows - Header rows of the template.
 * @returns {boolean} True when the failure is one of the header rows.
 */
export const isWhiteListHeaderFailure = (failure, headerRows) => isWhiteListHeaderRow(
    whiteListFailureToValues(failure),
    headerRows
);

/**
 * Titles the downloaded template holds on its first row. The upload endpoint
 * reads every row of the file as a resident, so this row is taken out before
 * uploading it and the grid of the import errors view only paints it as a guide.
 */
export const WHITE_LIST_TEMPLATE_HEADER = ['Name', 'Last Name', 'Email', 'Apartment'];

/**
 * Type the upload endpoint expects, the sheets rebuilt for it travel with it.
 */
const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Turns the rows of one white list into the file the upload endpoint reads.
 * Every row of it is a resident, the titles of the columns are never part of it.
 *
 * @param {Array} rows - Rows of the white list, in WHITE_LIST_IMPORT_COLUMNS order.
 * @returns {File} Excel the upload endpoint is called with.
 */
export const buildWhiteListImportFile = (rows) => {
    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, WHITE_LIST_IMPORT_SHEET_NAME);
    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    return new File([buffer], WHITE_LIST_IMPORT_FILE_NAME, { type: XLSX_MIME_TYPE });
};

/**
 * Rebuilds the template the user picked without the titles of the columns and
 * without the rows left empty, the endpoint reads every row it receives as one
 * more resident. The file is uploaded as it was picked when it cannot be read or
 * when it does not hold the header of the template.
 *
 * @param {File} file - Template picked on the automatic tab.
 * @returns {Promise<File>} File the upload endpoint is called with.
 */
export const whiteListFileWithoutHeader = (file) => new Promise((resolve) => {
    if (!file) {
        resolve(file);
        return;
    }

    const reader = new FileReader();

    reader.onload = (event) => {
        try {
            const workbook = XLSX.read(event.target.result, { type: 'array' });
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' })
                .map((row) => whiteListSheetRowToValues(row))
                .filter((row) => row.some((cell) => cell.trim() !== ''))
                .filter((row) => !isWhiteListHeaderRow(row, [WHITE_LIST_TEMPLATE_HEADER]));

            // Nothing left to send, the file goes as it was picked so the
            // endpoint answers what is wrong with it
            resolve(rows.length > 0 ? buildWhiteListImportFile(rows) : file);
        } catch {
            resolve(file);
        }
    };

    reader.onerror = () => resolve(file);

    reader.readAsArrayBuffer(file);
});

/**
 * Form model shared by the create and the update building modals, it holds
 * every input of the form. The id only travels on the update payload.
 */
export const BUILDING_FORM_MODEL = {
    id: null,
    name: '',
    status: '',
    zoneId: '',
    latitude: '',
    longitude: '',
    searchLocation: '',
    /**
     * Payload of building-service/v1/building/create.
     *
     * @returns {Object} Body expected by the create endpoint.
     */
    toRegister: function () {
        return {
            statusId: this.status,
            zoneId: this.zoneId,
            name: this.name,
            latitude: Number(this.latitude),
            longitude: Number(this.longitude),
        }
    },
    /**
     * Payload of building-service/v1/building/update.
     *
     * @returns {Object} Body expected by the update endpoint.
     */
    toUpdate: function () {
        return {
            ...(this.id !== null && { id: this.id }),
            zoneId: this.zoneId,
            statusId: this.status,
            name: this.name,
            latitude: Number(this.latitude),
            longitude: Number(this.longitude),
        }
    },
    /**
     * Fills the form with the building returned by the service, the status
     * comes as an object on the detail and as an id on the list row.
     *
     * @param {Object} data - Building coming from the backend.
     * @returns {Object} Form model ready to be edited.
     */
    set: function (data) {
        return {
            ...this,
            id: data?.id ?? null,
            name: data?.name ?? '',
            status: data?.status?.id ?? data?.statusId ?? data?.status ?? '',
            zoneId: data?.zone?.id ?? data?.zoneId ?? '',
            latitude: data?.latitude ?? data?.latitud ?? '',
            longitude: data?.longitude ?? data?.longitud ?? '',
            searchLocation: ''
        }
    },
    isValid: function () {
        return this.name !== '' && this.status !== '' && this.zoneId !== ''
            && this.latitude !== '' && this.longitude !== '';
    }
}

export const BUILDING_MODEL = {
    id:null,
    approvalFlowName: '',
    department: null,
    module: null,
    toRegister: function (levels) {
        return {
            id:this.id,
            approvalFlowName: this.approvalFlowName,
            departmentDTO: {id:this.department},
            moduleDTO: {id:this.module},
            levels: levels.map(e => {
                return {
                    ...e,
                    approvers: e.approvers.map(row => {
                    return { user: { id: row.id, name: row.label } }
                })}
            })
        }
    },
    set: function (data) {
        return {
            ...this,
            id:data.id,
            approvalFlowName: data.approvalFlowName,
            department: data.departmentDTO?.id,
            module: data.moduleDTO.id,
        }
    },
    isValid: function () {
        return this.approvalFlowName !== '' && this.module != null;
    },
    isValidLevels: function (levels) {
        return levels.filter(e => e.approvers.length === 0).length <= 0;
    }
}


export const LEVEL_MODEL = {
    id:null,
    levelNumber: 1,
    approvalCondition: 'ONE_IS_ENOUGH',
    approvers: [],
    set: function (data) {
        return data?.map(e => {
            return {
                ...e,
                approvers: e?.approvers?.length > 0 ? e.approvers.map(x => {
                    return {id: x.user.id, label: userDataTransform(x.user)}
                }): []
            }
        }).sort((a, b) => a.levelNumber - b.levelNumber);
    },
    isValid: function (levels) {
        return levels.filter(e => e.approvers.length === 0).length <= 0;
    }
}

export const SCOPE_MODEL = () => {
    return [
        { id: 'ALL_MUST_APPROVE', name: t('all_must_approve')},
        { id: 'ONE_IS_ENOUGH', name: t('one_is_enough')},
    ];
};

/**
 * Chart.js plugin factory that writes the total inside the doughnut hole,
 * with its label right under the number.
 *
 * The chart is built once with the plugin it is given, only its data and its
 * options are refreshed afterwards, so the values are read from
 * options.plugins.doughnutCenterText and the arguments are only the fallback of
 * the first paint. DOUGHNUT_CENTER_TEXT_OPTIONS puts them there.
 *
 * @param {number} total - Value shown as the big number
 * @param {string} label - Word painted under the total, already translated
 */
export const doughnutCenterTextPlugin = ({ total, label }) => ({
    id: 'doughnutCenterText',
    afterDatasetsDraw: (chart) => {
        // The first arc gives the center of the ring, the legend does not shift it
        const arc = chart.getDatasetMeta(0)?.data?.[0];
        if (!arc) return;

        const centerText = chart?.options?.plugins?.doughnutCenterText || {};
        const currentTotal = centerText?.total ?? total;
        const currentLabel = centerText?.label ?? label;

        const { ctx } = chart;
        const fontFamily = getComputedStyle(chart.canvas).fontFamily;

        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        ctx.font = `700 24px ${fontFamily}`;
        ctx.fillStyle = colors.general950;
        ctx.fillText(currentTotal, arc.x, arc.y - 9);

        ctx.font = `400 12px ${fontFamily}`;
        ctx.fillStyle = colors.shades600;
        ctx.fillText(currentLabel, arc.x, arc.y + 12);

        ctx.restore();
    }
});

/**
 * Pie options reused for the building doughnuts, the cutout is widened
 * so the total fits inside the hole.
 */
export const BUILDING_DOUGHNUT_CUTOUT = '68%';

/**
 * Verification resume cards of the building detail, the totals are the residents
 * counted by building-service/v1/building/get/{id}.
 *
 * @param {Object} buildingDetail - Detail of the building being rendered.
 * @returns {Object} Cards of the resume, grouped by section.
 */
export const getBuildingUsersCardModel = (buildingDetail) => ({
    SUMMARY: {
        classCard: "background-shades-50",
        list: [
            {
                id: RESIDENT_STATUS.VERIFIED,
                titleTrans: "verified_owners",
                titleTransShort: "",
                statusId: '',
                total: buildingDetail?.validatedResidents ?? 0,
                cols: "col-6",
                classCard: "card-success-150-50",
                Icon:IconApprovedCard,
                prop: "",
                model: "",
                color: colors.success100,
                // Same border the classCard paints, card-success-150-50
                borderColor: colors.success150,
                fontColor: "font-color-success-700",
                infoColor: "icon-color-success-700 w-16px h-16px",
            },
            {
                id: RESIDENT_STATUS.UNVERIFIED,
                titleTrans: "unverified_owners",
                titleTransShort: "",
                statusId: "",
                total: buildingDetail?.pendingValidationResidents ?? 0,
                cols: "col-6",
                classCard: "card-warning-150-50",
                Icon:IconDashedCircleList,
                prop: "",
                model: "",
                color: colors.warning100,
                // Same border the classCard paints, card-warning-150-50
                borderColor: colors.warning150,
                fontColor: "font-color-warning-700",
                infoColor: "icon-color-warning-700 w-16px h-16px",
            },
        ]
    }
})

/**
 * Puts together the summaries returned by the white list endpoints so the
 * import errors view paints the failures of the file and of the manual rows on
 * the same grid.
 *
 * @param {Array} summaries - Summaries returned by the upload and create all endpoints.
 * @returns {Object} One summary with the totals added and the failures concatenated.
 */
export const mergeWhiteListSummaries = (summaries) => summaries
    .filter(Boolean)
    .reduce((merged, summary) => ({
        totalRecords: merged.totalRecords + (summary?.totalRecords || 0),
        savedRecords: merged.savedRecords + (summary?.savedRecords || 0),
        failedRecords: merged.failedRecords + (summary?.failedRecords || 0),
        failures: [...merged.failures, ...(summary?.failures || [])]
    }), { totalRecords: 0, savedRecords: 0, failedRecords: 0, failures: [] });
